<div class="main-view space-y-2 md:space-y-4">
    <div class="mb-4 flex flex-wrap items-center justify-start gap-2">
        <app-page-surface-header
            titleIcon="user"
            [title]="isEditMode ? 'Edit User' : 'Create User'"
            [subtitle]="isEditMode ? 'Edit User' : 'Create User'"
        />
    </div>

    <form [formGroup]="userForm" (ngSubmit)="onSubmit()" class="space-y-6">
        <div class="rounded-3xl bg-white/60 p-6 shadow-sm dark:bg-[#0e1726] md:p-8">
            <div class="space-y-1">
                <h2 class="text-lg font-bold text-black dark:text-white">User Details</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                    Provide user contact information and role accurately.
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    <span class="font-semibold text-red-600" aria-hidden="true">*</span> Required field
                </p>
            </div>

            <div class="my-6 border-t border-dashed border-gray-300 dark:border-[#191e3a]"></div>

            <div class="mb-8 flex flex-col items-start gap-4">
                <span class="text-sm font-medium text-gray-900 dark:text-white">Profile Picture</span>
                <div class="flex items-center gap-6">
                    <div
                        class="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-dark"
                    >
                        <img *ngIf="selectedFileUrl" [src]="selectedFileUrl" class="h-full w-full object-cover" alt="Preview" />
                        <icon-camera *ngIf="!selectedFileUrl" class="h-8 w-8 text-gray-400" />
                    </div>
                    <div class="flex flex-col gap-2">
                        <input
                            type="file"
                            id="profilePic"
                            class="hidden"
                            accept="image/*"
                            (change)="onFileSelected($event)"
                        />
                        <label for="profilePic" class="btn btn-sm btn-outline-primary cursor-pointer"> Upload Photo </label>
                        <button type="button" *ngIf="selectedFileUrl" class="btn btn-sm btn-outline-danger" (click)="removeFile()">
                            Remove
                        </button>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                <div>
                    <label for="fullName" class="mb-1.5 block text-sm font-medium text-gray-900 dark:text-white">
                        Full Name <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                    </label>
                    <input
                        id="fullName"
                        type="text"
                        formControlName="full_name"
                        class="form-input w-full rounded-lg border-white-light dark:border-[#191e3a]"
                        placeholder="Full Name"
                        aria-required="true"
                    />
                    <div
                        *ngIf="userForm.controls['full_name'].touched && userForm.controls['full_name'].invalid"
                        class="mt-1 text-xs text-danger"
                    >
                        Full Name is required
                    </div>
                </div>
                <div>
                    <label for="email" class="mb-1.5 block text-sm font-medium text-gray-900 dark:text-white">
                        Email ID <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                    </label>
                    <input
                        id="email"
                        type="email"
                        formControlName="email_id"
                        class="form-input w-full rounded-lg border-white-light dark:border-[#191e3a]"
                        placeholder="Email ID"
                        aria-required="true"
                    />
                    <div
                        *ngIf="userForm.controls['email_id'].touched && userForm.controls['email_id'].invalid"
                        class="mt-1 text-xs text-danger"
                    >
                        Valid Email is required
                    </div>
                </div>

                <div>
                    <app-intl-phone-field
                        label="Mobile Number"
                        inputId="mobileNumber"
                        name="mobileNumber"
                        placeholder="Enter mobile number"
                        [required]="true"
                        [(countryIso)]="phoneIso"
                        [(nationalNumber)]="phoneNational"
                    ></app-intl-phone-field>
                    <div *ngIf="phoneError" class="mt-1 text-xs text-danger">
                        {{ phoneError }}
                    </div>
                </div>

                <div>
                    <label for="role" class="mb-1.5 block text-sm font-medium text-gray-900 dark:text-white">
                        Role <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                    </label>
                    <select
                        id="role"
                        formControlName="role_id"
                        class="form-select w-full rounded-lg border-white-light dark:border-[#191e3a]"
                        aria-required="true"
                    >
                        <option [ngValue]="null" disabled selected>Select Role</option>
                        <option *ngFor="let role of roles" [value]="role.id">
                            {{ role.role_name }}
                        </option>
                    </select>
                    <div
                        *ngIf="userForm.controls['role_id'].touched && userForm.controls['role_id'].invalid"
                        class="mt-1 text-xs text-danger"
                    >
                        Role is required
                    </div>
                </div>
            </div>

            <div class="mt-8 flex flex-wrap justify-end gap-3 border-t border-dashed border-gray-300 pt-6 dark:border-[#191e3a]">
                <a routerLink="/manage-admin-users" class="btn rounded-full bg-white px-6 text-black">Cancel</a>
                <button
                    type="submit"
                    [disabled]="isSubmitting"
                    class="btn rounded-full bg-[#303030] px-8 font-semibold text-white shadow-lg disabled:opacity-50"
                >
                    {{ isSubmitting ? 'Saving...' : 'Save' }}
                </button>
            </div>
        </div>
    </form>

    <dialog
        *ngIf="showSuccessModal"
        open
        class="fixed inset-0 z-[200] m-0 flex h-full max-h-none w-full max-w-none items-center justify-center border-0 bg-transparent p-4 !mt-0"
        aria-labelledby="create-admin-user-success-title"
        (keydown.escape)="closeSuccessModal()"
    >
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[1px]" aria-hidden="true"></div>
        <div class="relative z-10 w-full max-w-md rounded-3xl bg-white px-8 pb-10 pt-14 text-center shadow-2xl dark:bg-[#0e1726]">
            <button
                type="button"
                class="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-[#18214d] transition hover:bg-gray-100 dark:text-white-dark dark:hover:bg-white/10"
                (click)="closeSuccessModal()"
                aria-label="Close"
            >
                <icon-x-circle class="size-5 hover:text-danger" />
            </button>

            <div class="mx-auto mb-6 flex h-[7.5rem] w-[7.5rem] items-center justify-center">
                <svg
                    class="text-emerald-500"
                    width="120"
                    height="120"
                    viewBox="0 0 120 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <g transform="translate(60,60)">
                        <line
                            x1="0"
                            y1="-38"
                            x2="0"
                            y2="-50"
                            stroke="currentColor"
                            stroke-width="3.5"
                            stroke-linecap="round"
                            transform="rotate(0)"
                        />
                        <line
                            x1="0"
                            y1="-38"
                            x2="0"
                            y2="-50"
                            stroke="currentColor"
                            stroke-width="3.5"
                            stroke-linecap="round"
                            transform="rotate(45)"
                        />
                        <line
                            x1="0"
                            y1="-38"
                            x2="0"
                            y2="-50"
                            stroke="currentColor"
                            stroke-width="3.5"
                            stroke-linecap="round"
                            transform="rotate(90)"
                        />
                        <line
                            x1="0"
                            y1="-38"
                            x2="0"
                            y2="-50"
                            stroke="currentColor"
                            stroke-width="3.5"
                            stroke-linecap="round"
                            transform="rotate(135)"
                        />
                        <line
                            x1="0"
                            y1="-38"
                            x2="0"
                            y2="-50"
                            stroke="currentColor"
                            stroke-width="3.5"
                            stroke-linecap="round"
                            transform="rotate(180)"
                        />
                        <line
                            x1="0"
                            y1="-38"
                            x2="0"
                            y2="-50"
                            stroke="currentColor"
                            stroke-width="3.5"
                            stroke-linecap="round"
                            transform="rotate(225)"
                        />
                        <line
                            x1="0"
                            y1="-38"
                            x2="0"
                            y2="-50"
                            stroke="currentColor"
                            stroke-width="3.5"
                            stroke-linecap="round"
                            transform="rotate(270)"
                        />
                        <line
                            x1="0"
                            y1="-38"
                            x2="0"
                            y2="-50"
                            stroke="currentColor"
                            stroke-width="3.5"
                            stroke-linecap="round"
                            transform="rotate(315)"
                        />
                    </g>
                    <circle cx="60" cy="60" r="28" fill="#22c55e" />
                    <path
                        d="M48 61 L56 69 L74 49"
                        stroke="white"
                        stroke-width="3.2"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </div>

            <h2 id="create-admin-user-success-title" class="text-lg font-bold text-black dark:text-white md:text-xl">
                User Successfully {{ isEditMode ? 'Updated' : 'Created' }}
            </h2>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                The user has been {{ isEditMode ? 'updated' : 'created' }} successfully.
            </p>
        </div>
</dialog>
